import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import NewsletterApi from "../../Api/NewsletterApi";
import CommonApi from "../../Api/CommonApi";
import FormApi from "../../Api/FormApi";
import EventApi from "../../Api/EventApi";
import { NewsletterDto } from "../../Models/NewsletterDto";
import { Subscription } from "../../Models/Subscription";
import { FormDto } from "../../Models/FormDto";
import { Event } from "../../Models/Event";

const AdminNewsletterListPage: React.FC = () => {
  const [newsletters, setNewsletters] = useState<NewsletterDto[]>([]);
  const [filteredNewsletters, setFilteredNewsletters] = useState<
    NewsletterDto[]
  >([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [forms, setForms] = useState<FormDto[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<
    "all" | "sent" | "scheduled" | "draft"
  >("all");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [newsletterRes, subscriptionRes, formsRes, eventsRes] =
          await Promise.all([
            NewsletterApi.getAll(),
            CommonApi.getSubscriptions(),
            FormApi.getAllForms(),
            EventApi.getEventList(),
          ]);
        setNewsletters(newsletterRes.data);
        setSubscriptions(subscriptionRes.data);
        setForms(formsRes.data);
        setEvents(eventsRes.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter newsletters based on search term and status
  useEffect(() => {
    let filtered = newsletters;

    // Filter by status
    if (filterStatus !== "all") {
      filtered = filtered.filter((newsletter) => {
        switch (filterStatus) {
          case "sent":
            return newsletter.dateSent;
          case "scheduled":
            return newsletter.dateScheduled && !newsletter.dateSent;
          case "draft":
            return !newsletter.dateScheduled && !newsletter.dateSent;
          default:
            return true;
        }
      });
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter((newsletter) =>
        newsletter.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredNewsletters(filtered);
  }, [newsletters, searchTerm, filterStatus]);

  const sendNow = (id: number, remaining = false) => {
    const message = remaining
      ? "Send to remaining form/event recipients who have not received this newsletter yet? Already-sent emails will be skipped."
      : "Are you sure you want to send this newsletter now?";
    if (window.confirm(message)) {
      NewsletterApi.sendNow(id, remaining)
        .then((res) => {
          const count = (res.data as { sentCount?: number })?.sentCount;
          alert(
            remaining
              ? `Sent to ${count ?? 0} remaining recipient(s).`
              : "Newsletter send started."
          );
          NewsletterApi.getAll().then((listRes) => {
            setNewsletters(listRes.data);
          });
        })
        .catch((err) => {
          alert(err?.response?.data || err.message || "Failed to send newsletter");
        });
    }
  };

  const handleDelete = (id: number) => {
    if (window.confirm("Are you sure you want to delete this newsletter?")) {
      NewsletterApi.delete(id).then(() => {
        setNewsletters((prev) => prev.filter((n) => n.id !== id));
      });
    }
  };

  const getGroupsDisplay = (newsletter: NewsletterDto): string => {
    const parts: string[] = [];

    // Base audience: all members vs specific subscription levels
    if (newsletter.sendToAll) {
      parts.push("All Members");
    } else {
      if (
        !newsletter.subscriptionIds ||
        newsletter.subscriptionIds.length === 0
      ) {
        parts.push("No subscription levels");
      } else {
        const subscriptionNames = newsletter.subscriptionIds
          .map((id) => subscriptions.find((sub) => sub.id === id)?.name)
          .filter(Boolean);
        if (subscriptionNames.length === 0) {
          parts.push(`${newsletter.subscriptionIds.length} level(s)`);
        } else if (subscriptionNames.length <= 3) {
          parts.push(subscriptionNames.join(", "));
        } else {
          const remaining = subscriptionNames.length - 2;
          parts.push(
            `${subscriptionNames.slice(0, 2).join(", ")} +${remaining} more`
          );
        }
      }
    }

    // Form filter: restricts to users who submitted these forms
    const formIds = newsletter.formIds ?? [];
    if (formIds.length > 0) {
      const formNames = formIds
        .map((id) => {
          const f = forms.find((form) => form.formId === id);
          return f?.name ?? f?.title ?? "";
        })
        .filter(Boolean);
      const formLabel =
        formNames.length > 0
          ? formNames.join(", ")
          : `${formIds.length} form(s)`;
      parts.push(`Form: ${formLabel}`);
    }

    // Event filter: restricts to users registered for these events
    const eventIds = newsletter.eventIds ?? [];
    if (eventIds.length > 0) {
      const eventNames = eventIds
        .map((id) => events.find((e) => e.id === id)?.name)
        .filter(Boolean);
      const eventLabel =
        eventNames.length > 0
          ? eventNames.join(", ")
          : `${eventIds.length} event(s)`;
      parts.push(`Event: ${eventLabel}`);
    }

    return parts.join(" → ");
  };
  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6 w-full">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Newsletters</h1>
        <button
          className="bg-blue-600 text-white px-4 py-2 rounded"
          onClick={() => navigate("/newsletters/new")}
        >
          + New Newsletter
        </button>
      </div>

      {/* Search and Filter Controls */}
      <div className="mb-4 flex flex-col sm:flex-row gap-4">
        {/* Search Input */}
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search newsletters by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Status Filter */}
        <div>
          <select
            value={filterStatus}
            onChange={(e) =>
              setFilterStatus(
                e.target.value as "all" | "sent" | "scheduled" | "draft"
              )
            }
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Newsletters</option>
            <option value="sent">Sent</option>
            <option value="scheduled">Scheduled</option>
            <option value="draft">Draft</option>
          </select>
        </div>
      </div>

      {/* Results Count */}
      <div className="mb-4 text-sm text-gray-600">
        Showing {filteredNewsletters.length} of {newsletters.length} newsletters
      </div>

      <div className="overflow-auto border rounded">
        {filteredNewsletters.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {searchTerm || filterStatus !== "all" ? (
              <>
                <p className="text-lg mb-2">No newsletters found</p>
                <p className="text-sm">
                  Try adjusting your search term or filter criteria
                </p>
              </>
            ) : (
              <p className="text-lg">No newsletters available</p>
            )}
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-200">
              <tr>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2">Groups</th>
                <th className="text-left px-4 py-2">Scheduled</th>
                <th className="text-left px-4 py-2">Sent</th>
                <th className="text-left px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredNewsletters.map((n) => (
                <tr key={n.id} className="border-t">
                  <td className="px-4 py-2">{n.name}</td>
                  <td className="px-4 py-2">{getGroupsDisplay(n)}</td>
                  <td className="px-4 py-2">
                    {n.dateScheduled
                      ? new Date(n.dateScheduled).toLocaleString()
                      : "—"}
                  </td>
                  <td className="px-4 py-2">
                    {n.dateSent ? new Date(n.dateSent).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-2 space-x-2">
                    <Link
                      to={`/newsletters/edit/${n.id}`}
                      className="text-blue-600 hover:underline"
                    >
                      Edit
                    </Link>
                    {!n.dateSent && (
                      <button
                        onClick={() => n.id !== undefined && sendNow(n.id)}
                        className="text-green-600 hover:underline"
                      >
                        Send Now
                      </button>
                    )}
                    {n.dateSent &&
                      ((n.formIds && n.formIds.length > 0) ||
                        (n.eventIds && n.eventIds.length > 0)) && (
                        <button
                          onClick={() =>
                            n.id !== undefined && sendNow(n.id, true)
                          }
                          className="text-green-700 hover:underline"
                        >
                          Send remaining
                        </button>
                      )}
                    {!n.dateSent && (
                      <button
                        onClick={() => n.id !== undefined && handleDelete(n.id)}
                        className="text-red-600 hover:underline"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminNewsletterListPage;
